import { yupResolver } from '@hookform/resolvers/yup'
import { compareAsc } from '@if/core/date'
import { EFinancingProgram, EPaymentPlan, EWorksheetStatus } from '@if/core/types'
import { normalizeNumber } from '@if/core/utils'
import { Button, Grid, Paper, Stack } from '@mui/material'
import { Breadcrumb, PageError } from '@src/components'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useUpdateAutoRslaWorksheet } from '@src/data/api/auto-rsla-worksheet/auto-rsla-worksheet-api'
import { useGetMerchantById } from '@src/data/api/merchant/merchant-api'
import { useBusinessErrors, useSetBusinessErrors } from '@src/data/selectors/app-config-selectors'
import {
  AutoRslaWorksheet,
  EditAutoRslaWorksheetDto,
  EditAutoRslaWorksheetDtoSchema,
} from '@src/data/types/AutoRslaWorksheet'
import { reportErrorToConsole } from '@src/services/error-logger'
import { extractWorksheetSaveErrors } from '@src/services/worksheet-save-error'
import { FieldError, FieldErrorsImpl, Merge, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Constants, Vehicle } from '../../data/types'
import { useFirstPaymentDateOptions } from '../EditWorksheet/editWorksheet-hooks'
import { useMaxPaymentAmountWithAgentLeeway, useMaxPaymentError } from '../ViewWorksheet/viewWorksheet-hooks'
import TransactionForm from './components/TransactionForm'
import VehicleForm from './components/VehicleForm'

const EditAutoRslaWorksheetPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Get worksheet from context
  const { creditApplication, worksheet: baseWorksheet } = useCreditApplicationData()
  const worksheet = baseWorksheet as AutoRslaWorksheet | undefined
  const {
    register,
    handleSubmit,
    watch,
    control,
    getValues,
    setValue,
    formState: { errors },
    reset,
  } = useForm<EditAutoRslaWorksheetDto>({
    mode: 'onBlur', // déclenche les validations Après que l'usager ait quitté le champ
    defaultValues: EditAutoRslaWorksheetDtoSchema.getDefault() as unknown as EditAutoRslaWorksheetDto,
    resolver: yupResolver(EditAutoRslaWorksheetDtoSchema),
  })

  // Fetch merchant using direct API call
  const [merchant] = useGetMerchantById(creditApplication?.merchantId ?? '')
  const [updateAutoRslaWorksheet, isLoading] = useUpdateAutoRslaWorksheet()
  const breadCrumbs = [
    { path: '/', label: t('breadcrumbs.home') },
    { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
    {
      path: `/Applications/${EFinancingProgram.AutoRsla}/${creditApplication?.id}/view`,
      label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
    },
    { path: '#', label: t('worksheet.financing') },
  ]
  const error = useBusinessErrors()
  const setBusinessErrors = useSetBusinessErrors()
  const easternTime = new Date().toLocaleString('en-US', { timeZone: 'America/Toronto' })
  const currentDateAsEasternTimeZone = new Date(easternTime)
  const paymentFrequency = watch('paymentFrequency')
  const deliveryOn = watch('deliveryOn')
  const paymentAmountForFrequency = watch('paymentAmountForFrequency')
  const maxPaymentAmount = useMaxPaymentAmountWithAgentLeeway(
    EPaymentPlan.RegularDailyInterests,
    creditApplication?.finalCreditDecision?.maxPmtAmount ?? 0,
  )
  const maxPaymentForFrequencyError = useMaxPaymentError(
    paymentFrequency,
    paymentAmountForFrequency,
    maxPaymentAmount,
    Constants.financingProgramConfigs[EFinancingProgram.AutoRsla].validateMaxPaymentAmountInWorksheet,
  )
  const onFormSubmitted = async (data: EditAutoRslaWorksheetDto) => {
    const expiry = new Date(creditApplication!.expiresOn)
    const amountRequested = normalizeNumber(watch('amountRequested')?.toString()) ?? 0
    const deliveryOnDate = new Date(`${getValues('deliveryOn')}T20:00:00`) // 20PM Local Time
    const deliveryOnUTC = new Date(`${getValues('deliveryOn')}T00:00:00`)
    const deliveryOnDateAsEasternTimeZone = new Date(
      deliveryOnDate.toLocaleString('en-US', { timeZone: 'America/Toronto' }),
    )
    const baseActivationDateAsEasternTimeZone = new Date(
      currentDateAsEasternTimeZone.getFullYear(),
      currentDateAsEasternTimeZone.getMonth(),
      currentDateAsEasternTimeZone.getDate(),
      0,
      0,
      0,
    )

    if (compareAsc(deliveryOnDateAsEasternTimeZone, baseActivationDateAsEasternTimeZone) === -1) {
      setBusinessErrors([{ message: 'worksheetCommon.activationDateInPast' }])
      return
    } else if (compareAsc(deliveryOnUTC, expiry) === 1) {
      setBusinessErrors([{ message: t('worksheetCommon.deliveryOnEarlierExpiration') }])
      return
    }
    data.amountRequested = amountRequested
    data.status = EWorksheetStatus.Active
    data.vehicle.pricingCategory = 'average'
    const adjustedWholePrices = data.vehicle.pricing.adjustedWholePrices
    if (adjustedWholePrices.avg > 0) {
      data.vehicle.thirdPartyEvaluationValue = adjustedWholePrices.avg
    }

    try {
      await updateAutoRslaWorksheet(data)
      // React-Query will invalidate fullCreditApplication queries automatically
      navigate(`/Applications/${EFinancingProgram.AutoRsla}/${creditApplication!.id}/view`, { replace: true })
    } catch (error) {
      reportErrorToConsole(error)
      setBusinessErrors(extractWorksheetSaveErrors(error, 'worksheet.saveFailed'))
    }
  }

  const { minFirstPaymentDate, maxFirstPaymentDate } = useFirstPaymentDateOptions({
    financingProgramId: creditApplication?.financingProgramId,
    creditApplicationId: creditApplication?.id,
    deliveryOn,
    paymentFrequency,
    isHighEstimate: false,
    setValue,
    getValues,
  })

  if (!creditApplication || !worksheet) return null

  return (
    <div>
      <Breadcrumb trees={breadCrumbs} />
      <form onSubmit={handleSubmit(onFormSubmitted, reportErrorToConsole)}>
        <div style={{ height: '100%' }}>
          <Paper>
            <PageError errors={error} />
            <input type="hidden" value=" " {...register('id')} />
            <Grid
              sx={{ flexDirection: 'row' }}
              size={{
                xs: 6,
                md: 3.9,
              }}
            >
              <Stack direction="row" justifyContent="flex-end" spacing={3}>
                <div style={{ marginTop: '1%' }}>
                  <Button variant="contained" type="submit" disabled={isLoading || maxPaymentForFrequencyError != null}>
                    {t('common.save')}
                  </Button>
                </div>
              </Stack>
            </Grid>
            <VehicleForm
              defaultVehicle={worksheet.vehicle}
              merchant={merchant}
              control={control}
              getValues={getValues}
              setValue={setValue}
              register={register}
              watch={watch}
              errors={errors.vehicle as Merge<FieldError, FieldErrorsImpl<Vehicle>>}
              title={t('worksheet.vehicle')}
            />
            <TransactionForm
              control={control}
              currentCreditApplication={creditApplication}
              reset={reset}
              register={register}
              watch={watch}
              worksheet={worksheet}
              errors={errors as Merge<FieldError, FieldErrorsImpl<EditAutoRslaWorksheetDto>>}
              creditApplicationInterestRate={creditApplication.finalCreditDecision.interestRate}
              minFirstPaymentDate={minFirstPaymentDate}
              maxFirstPaymentDate={maxFirstPaymentDate}
            />
          </Paper>
        </div>
      </form>
    </div>
  )
}

export default EditAutoRslaWorksheetPage
